import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Building2, Save, MapPin, FileBadge, Image as ImageIcon, Truck } from "lucide-react";

const ZONE_OPTIONS = [
  "IT-Lombardia", "IT-Veneto", "IT-Emilia-Romagna", "IT-Toscana", "IT-Lazio",
  "IT-Campania", "IT-Sicilia", "IT-Piemonte", "IT-Puglia", "IT-Marche",
  "EU-DE", "EU-FR", "EU-ES", "EU-AT", "EU-CH",
];

export default function B2BAnagrafica() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verified, setVerified] = useState(false);
  const [form, setForm] = useState({
    company_name: "", vat_number: "", address: "", city: "", postal_code: "", country: "IT",
    contact_person: "", contact_email: "", contact_phone: "", iban: "",
    haccp_license: "", excise_license: "", logo_url: "",
    delivery_zones: [], preferred_pricing: "",
  });

  useEffect(() => {
    api.get("/b2b/profile").then((r) => {
      setForm({ ...form, ...r.data.profile });
      setVerified(r.data.verified);
      setLoading(false);
    }).catch(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const toggleZone = (z) => {
    setForm((f) => ({
      ...f,
      delivery_zones: f.delivery_zones.includes(z)
        ? f.delivery_zones.filter((x) => x !== z)
        : [...f.delivery_zones, z],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/b2b/profile", form);
      toast.success("Anagrafica aggiornata");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore aggiornamento");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="container-xl py-12 text-muted-foreground">Caricamento…</div>;

  return (
    <div data-testid="b2b-anagrafica-page" className="container-xl py-10 max-w-5xl">
      <div className="flex items-center gap-3 mb-2">
        <Building2 className="h-6 w-6 text-[hsl(var(--accent))]" />
        <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--accent))] font-semibold">Anagrafica</div>
      </div>
      <h1 className="editorial-heading text-4xl md:text-5xl mb-2">Profilo azienda</h1>
      <p className="text-muted-foreground mb-6">
        Compila tutti i dati per pubblicare lotti e accedere al marketplace B2B.
        {verified && <span className="ml-2 inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-800 rounded-full">Verificato</span>}
      </p>

      <form onSubmit={submit} className="space-y-8">
        {/* DATI AZIENDALI */}
        <section className="bg-card border border-border rounded-sm p-6">
          <h2 className="text-sm uppercase tracking-[0.2em] text-muted-foreground mb-4 flex items-center gap-2">
            <Building2 className="h-4 w-4" /> Dati aziendali
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            <Input label="Ragione sociale *" value={form.company_name} onChange={onChange("company_name")} required testid="b2b-anag-company" />
            <Input label="Partita IVA *" value={form.vat_number} onChange={onChange("vat_number")} required testid="b2b-anag-vat" />
            <Input label="Indirizzo *" value={form.address} onChange={onChange("address")} required testid="b2b-anag-address" />
            <div className="grid grid-cols-3 gap-2">
              <Input label="CAP" value={form.postal_code} onChange={onChange("postal_code")} />
              <Input label="Città *" value={form.city} onChange={onChange("city")} required />
              <Input label="Paese" value={form.country} onChange={onChange("country")} maxLength={3} />
            </div>
            <Input label="IBAN" value={form.iban} onChange={onChange("iban")} testid="b2b-anag-iban" />
            <Input label="Logo URL" value={form.logo_url} onChange={onChange("logo_url")} placeholder="https://..." />
          </div>
        </section>

        {/* REFERENTE */}
        <section className="bg-card border border-border rounded-sm p-6">
          <h2 className="text-sm uppercase tracking-[0.2em] text-muted-foreground mb-4">Referente</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <Input label="Nome referente *" value={form.contact_person} onChange={onChange("contact_person")} required />
            <Input label="Email *" type="email" value={form.contact_email} onChange={onChange("contact_email")} disabled />
            <Input label="Telefono *" value={form.contact_phone} onChange={onChange("contact_phone")} required />
          </div>
        </section>

        {/* LICENZE */}
        <section className="bg-card border border-border rounded-sm p-6">
          <h2 className="text-sm uppercase tracking-[0.2em] text-muted-foreground mb-4 flex items-center gap-2">
            <FileBadge className="h-4 w-4" /> Licenze fiscali
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            <Input label="Codice HACCP" value={form.haccp_license} onChange={onChange("haccp_license")} placeholder="es. HACCP-IT-2024-XXXX" />
            <Input label="Codice deposito accise" value={form.excise_license} onChange={onChange("excise_license")} placeholder="es. IT00000000000A" />
          </div>
        </section>

        {/* ZONE CONSEGNA */}
        <section className="bg-card border border-border rounded-sm p-6">
          <h2 className="text-sm uppercase tracking-[0.2em] text-muted-foreground mb-4 flex items-center gap-2">
            <Truck className="h-4 w-4" /> Zone di consegna
          </h2>
          <div className="flex flex-wrap gap-2">
            {ZONE_OPTIONS.map((z) => {
              const active = form.delivery_zones.includes(z);
              return (
                <button
                  key={z}
                  type="button"
                  onClick={() => toggleZone(z)}
                  data-testid={`b2b-anag-zone-${z}`}
                  className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                    active ? "bg-[hsl(var(--accent))] text-black border-[hsl(var(--accent))]" : "bg-transparent border-border text-foreground hover:border-[hsl(var(--accent))]"
                  }`}
                >
                  {z}
                </button>
              );
            })}
          </div>
        </section>

        {/* NOTE PREFERENZIALI */}
        <section className="bg-card border border-border rounded-sm p-6">
          <h2 className="text-sm uppercase tracking-[0.2em] text-muted-foreground mb-4">Listino preferenziale / note</h2>
          <textarea
            value={form.preferred_pricing}
            onChange={onChange("preferred_pricing")}
            rows={4}
            placeholder="Volumi annui, sconti negoziati, condizioni di pagamento, segmenti preferiti…"
            className="w-full px-3 py-2 border border-border rounded-sm bg-background text-foreground"
          />
        </section>

        <button
          type="submit"
          disabled={saving}
          data-testid="b2b-anag-submit"
          className="inline-flex items-center gap-2 px-6 py-3 bg-[hsl(var(--accent))] text-black font-semibold rounded-sm hover:bg-[hsl(var(--accent))]/90 transition-colors disabled:opacity-50"
        >
          <Save className="h-4 w-4" />
          {saving ? "Salvataggio…" : "Salva anagrafica"}
        </button>
      </form>
    </div>
  );
}

function Input({ label, testid, ...rest }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">{label}</span>
      <input
        {...rest}
        data-testid={testid}
        className="w-full px-3 py-2 border border-border rounded-sm bg-background text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
      />
    </label>
  );
}
